import React, { useState } from "react";

import { useNavigate } from "react-router-dom";
import { Layout, message } from "antd";
import CustomSelect from "../../components/ui/CustomSelect";
import FileUploaderComponent from "../../components/ui/FileUploader";
import infoOutline from "../../assets/images/infoOutline.svg";
import AppHeader from "../../layouts/AppHeader";
import { useAuth } from "../../context/AuthContext";
import {
  create,
  createWithFile,
  update,
  updateWithFile,
} from "../../services/apiService"; // Import API service

const { Content } = Layout;

export default function FicaDocuments() {
  const navigate = useNavigate();
  const { userProfile, handleLogout } = useAuth();

  // For testing, use hardcoded userId, switch to userProfile.id for production
  const userId = userProfile?._id;

  const [step, setStep] = useState(1);
  const [selectedOption, setSelectedOption] = useState(null);
  const [frontIdFile, setFrontIdFile] = useState(null);
  const [backIdFile, setBackIdFile] = useState(null);
  const [addressFile, setAddressFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);

  const isProofOfIdComplete = frontIdFile && backIdFile;
  const isProofOfAddressComplete = addressFile;

  // ✅ Upload User Documents - Fix: Send `userId` in the body
  const uploadUserDocuments = async () => {
    try {
      setLoading(true);

      if (!frontIdFile || !backIdFile || !addressFile) {
        message.error("Please select all required files before uploading.");
        return false;
      }

      const formData = new FormData();

      formData.append("userId", userId);

      formData.append("ID_DOC_FRONT", frontIdFile, frontIdFile.name);
      formData.append("ID_DOC_BACK", backIdFile, backIdFile.name);
      formData.append("POA_DOC", addressFile, addressFile.name);

      const response = await createWithFile(
        "/api/v1/user/upload-documents",
        formData
      );

      if (response.status == 200 || response.status == 201) {
        return response;
      }

      message.error("Failed to upload documents.");
      throw new Error(`Failed to upload documents.`);
    } catch (error) {
      message.error("Could not upload documents. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Upload to Monday.com - Fix: userId should be in query params
  const uploadFilesToMonday = async () => {
    try {
      setLoading(true);

      // ✅ Fix: Include userId in query params
      const response = await update(
        `/api/v1/user/upload-lead?userId=${userId}`,
        {}
      );
      return response;
    } catch (error) {
      message.error("Failed to upload to documents.");
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Handle Next Button Click
  const handleNext = async () => {
    if (step === 1) {
      setStep(2);
    } else {
      try {
        const uploadResult = await uploadUserDocuments();
        if (uploadResult) {
          const mondayResult = await uploadFilesToMonday();
          if (mondayResult) {
            message.success("Successfully uploaded documents");
            navigate("/onboarding/finish", { replace: true });
          }
        }
      } catch (error) {
        //navigate("/");
      }
    }
  };

  return (
    <Layout className="flex flex-col min-h-screen">
      <AppHeader title="Verify ID" onBack={() => {}} onClose={handleLogout} />

      <Content className="flex-grow bg-white p-6 flex items-center justify-center">
        <div className="flex flex-col relative bg-white px-6">
          <main className="flex flex-1 flex-col items-center text-center mt-10">
            <h2 className="text-2xl font-bold text-brand-primary">
              {step === 1
                ? "Upload your proof of ID"
                : "Upload your proof of address"}
            </h2>

            <p className="text-sm text-gray-600 mt-2 mb-6">
              {step === 1
                ? "Select which type of ID document you’ll be uploading:"
                : "Be sure to upload a document that clearly shows your address."}
            </p>

            {step === 1 && (
              <>
                <CustomSelect
                  selectedOption={selectedOption}
                  setSelectedOption={setSelectedOption}
                />
                <div className="w-full max-w-xs mt-6 space-y-4">
                  <FileUploaderComponent
                    label="Front of ID card"
                    onFileUpload={(file) => {
                      setFrontIdFile(file);
                    }}
                  />

                  <FileUploaderComponent
                    label="Back of ID card"
                    onFileUpload={(file) => {
                      setBackIdFile(file);
                    }}
                  />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <div className="flex items-center space-x-2 mb-4">
                  <img src={infoOutline} alt="Info Icon" width="16" />
                  <p className="text-red-500 text-sm cursor-pointer">
                    What documents can I upload?
                  </p>
                </div>

                <div className="w-full max-w-xs mt-6 space-y-4">
                  <FileUploaderComponent
                    label="Proof of Address"
                    onFileUpload={setAddressFile}
                  />
                </div>
              </>
            )}

            <button
              className={`mt-6 w-full max-w-xs py-3 rounded-lg ${
                (step === 1 && !isProofOfIdComplete) ||
                (step === 2 && !isProofOfAddressComplete) ||
                loading
                  ? "btn-disable"
                  : "btn-primary"
              }`}
              onClick={handleNext}
              disabled={
                (step === 1 && !isProofOfIdComplete) ||
                (step === 2 && !isProofOfAddressComplete) ||
                loading
              }
            >
              {loading ? "Uploading..." : "Next"}
            </button>
          </main>
        </div>
      </Content>
    </Layout>
  );
}
